<template>
  <div class="editable-field" :class="`field-${fieldType}`">
    <div v-if="editMode" class="editor" v-click-outside="handleClickOutside">
      <div class="editor__field-container">
        <template v-if="fieldType === 'text'">
          <input
            :ref="`ref_${fieldName}`"
            :type="fieldType"
            @keydown.esc="rollbackChanges"
            @keydown.enter="saveChanges"
            v-model="internalValue"
            :placeholder="placeholder"/>
        </template>

        <template v-if="fieldType === 'url'">
          <input
            :ref="`ref_${fieldName}`"
            :type="fieldType"
            @keydown.esc="rollbackChanges"
            @keydown.enter="saveChanges"
            v-model="internalValue"
            :placeholder="placeholder"/>
        </template>

        <template v-if="fieldType === 'datetime'">
          <datetime class="field-datetime"
                    :ref="`ref_${fieldName}`"
                    :format="options.saveFormat || field.defaultDateTimeFormat"
                    type="datetime"
                    @close="saveDateTimeChanges"
                    v-model="internalValue">
          </datetime>
        </template>

        <template v-if="fieldType === 'textarea'">
          <textarea
            :ref="`ref_${fieldName}`"
            @keydown.esc="rollbackChanges"
            v-model="internalValue"
            :placeholder="placeholder"
            rows="4">
          </textarea>
        </template>

        <div v-if="fieldType === 'boolean'" class="options-container">
          <div v-for="(option, index) in field.constantItems" :key="`option${index}`" class="option">
            <input type="radio" :id="`option${index}`" :value="option.value" v-model="internalValue" @change="saveChanges">
            <label :for="`option${index}`">{{ option.label }}</label>
          </div>
        </div>

        <div v-if="fieldType === 'select'">
          <CSelect
            :add-wrapper-classes="['col-sm-12', 'p-0']"
            :value.sync="internalValue"
            @update:value="saveChanges"
            :options="items"
            placeholder="Please select"
            size="sm"
            custom
            horizontal />
        </div>
      </div>

      <div v-if="editToolsAllowed" class="edit-tools">
        <i title="Rollback changes"
           class="fa fa-remove icon icon-rollback"
           :class="{'active': isValueChanged}"
           @click="rollbackChanges"></i>

        <i title="Save changes"
           class="fa fa-check icon icon-save"
           :class="{'active': isValueChanged}"
           @click="saveChanges"></i>
      </div>
    </div>

    <div v-else class="viewer" @click="toEditMode">
      <div v-if="updating">
        <span class="viewer__content" :class="uniqueComponentClass">...</span>
      </div>

      <div v-else-if="originValue">
        <span class="viewer__content" :class="uniqueComponentClass">
          <template v-if="fieldType === 'boolean'">True</template>
          <template v-else-if="fieldType === 'datetime'">{{ datetimeOriginValue }} (UTC)</template>
          <template v-else>{{ originValue }}</template>
        </span>
      </div>

      <div v-else>
        <span class="viewer__content" :class="uniqueComponentClass">
          <template v-if="fieldType === 'boolean'">False</template>
          <template v-else>{{ placeholder }}</template>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import getNested from 'lodash-es/get';
import ClickOutside from 'vue-click-outside';
import { Datetime as DateTimePicker } from 'vue-datetime';
import 'vue-datetime/dist/vue-datetime.css';
import { DateTime } from 'luxon';


let componentId = 0;

// constants
const fieldTypes = [
  'text',
  'datetime',
  'textarea',
  'boolean',
  'select',
  'url',
];

export default {
  name: 'editable-field',

  directives: {
    ClickOutside,
  },

  components: {
    datetime: DateTimePicker,
  },

  props: {
    fieldType: {
      type: String,
      default: 'text',
      validator: function (value) {
        return fieldTypes.includes(value);
      },
    },

    fieldName: {
      type: String,
      default: '',
    },

    items: {
      type: Array,
      default: () => ([]),
    },

    entityData: {
      type: Object,
      default: () => ({}),
    },

    saveMethod: {
      type: Function,
      default: () => { console.error('saveMethod not specified'); },
    },

    placeholder: {
      type: String,
      default: 'Placeholder',
    },

    options: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    let choices = {
      boolean: [
        { value: true, label: 'True' },
        { value: false, label: 'False' },
      ],
    };

    componentId += 1;

    return {
      // each editable field have unique componentId on a page
      componentId: componentId,

      updating: false,
      editMode: false,
      internalValue: null,

      field: {
        constantItems: choices[this.fieldType] || [],
        defaultDateTimeFormat: 'y-MM-dd HH:mm:ss',
      },
    };
  },

  methods: {
    toEditMode() {
      this.setInitialValue();
      this.editMode = true;

      this.$nextTick(() => {
        let element = this.$refs[`ref_${this.fieldName}`];

        if(element ) {
          if(element.focus) {
            element.focus();
          }

          if(element.$el) {
            const inputElement = element.$el.querySelector('input');
            if(inputElement) {
              inputElement.focus();
            }
          }
        }
      });
    },

    setInitialValue() {
      if(this.fieldType === 'boolean') {
        this.internalValue = !!this.originValue;
      } else {
        this.internalValue = this.originValue;
      }
    },

    toViewMode() {
      this.editMode = false;
    },

    handleClickOutside(evt) {
      const targetElement = evt.target;

      const isViewerContentClicked = (
        targetElement.classList.contains('viewer__content') &&
        targetElement.classList.contains(this.uniqueComponentClass)
      )

      if(this.editMode && !isViewerContentClicked) {
        this.saveChanges();
      }
    },

    async saveDateTimeChanges() {
      if(this.internalValue) {
        await this.saveChanges();
      } else {
        this.rollbackChanges();
      }
    },

    async saveChanges() {
      this.editMode = false;
      this.updating = true;

      if(!this.isValueChanged) {
        // dummy update
        this.$emit('updated', this.entityData);
        this.updating = false;
        return;
      }

      const data = {
        ...this.entityData,
        [this.fieldName]: this.formattedInternalValue,
      };
      // console.log('saveChanges data', JSON.stringify(data, null, 2));

      let result = await this.saveMethod(data);
      if(result.error) {
        this.$emit('update-error', result);
      } else {
        this.$emit('updated', result);
      }
      this.updating = false;
    },

    rollbackChanges () {
      this.internalValue = this.originValue;
      this.editMode = false;
    },
  },

  computed: {
    uniqueComponentClass() {
      return `editable-field-${componentId}`;
    },

    datetimeOriginValue() {
      return DateTime
          .fromFormat(this.originValue, this.options.saveFormat)
          .toFormat(this.options.displayFormat);
    },

    originValue() {
      return getNested(this.entityData, this.fieldName, null);
    },

    formattedInternalValue() {
      if(this.fieldType === 'datetime') {
        // datetime picker doesn't store values in the provided format
        // so it's needed to convert it manually
        let datetime = DateTime.fromISO(this.internalValue).setZone('UTC');
        return datetime.toFormat(this.options.saveFormat || this.field.defaultDateTimeFormat);
      }

      if(typeof this.internalValue === 'string') {
        return this.internalValue.trim();
      }

      return this.internalValue;
    },

    isValueChanged() {
      return this.formattedInternalValue !== this.originValue;
    },

    isEmptyValue() {
      if(typeof this.formattedInternalValue === 'boolean') {
        return false;
      }

      return !!this.formattedInternalValue;
    },

    editToolsAllowed() {
      const fieldTypes = [
        'text',
        'url',
        'textarea',
        // 'boolean',
        // 'select',
      ];
      return fieldTypes.includes(this.fieldType);
    },
  },
}
</script>

<style scoped>
  .field-select >>> .form-group {
    margin: 0;
  }
  .field-select >>> .custom-select {
    box-shadow: none;
    -webkit-box-shadow: none;
    color: #2b2b2b;
  }

  .field-datetime >>> .vdatetime input {
    border: 1px solid #eeeeee;
  }
</style>

<style scoped lang="scss">
  .viewer {
    font-size: 15px;
    color: #474747;

    &__content {
      cursor: pointer;
      border-bottom: 1px dashed;
      max-width: 100%;
      display: inline-block;

      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .field-textarea &__content {
      white-space: unset;
    }

    &__content:hover {
      color: #0078d5;
      border-bottom-color: #0078d5;
    }
  }

  .editable-field {
    .editor {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &__field-container {
        width: 100%;
      }
    }

    &.field-textarea {
      .editor {
        align-items: flex-start;
      }
      textarea {
        width: 100%;
        padding: 5px 8px;
        border: 1px solid #c6c6c6;
        border-radius: 3px;
        outline: none;
      }
    }

    &.field-text input,
    &.field-url input, {
      width: 100%;
      padding: 1px 3px;
      outline: none;
      border: 1px solid #c6c6c6;
      border-radius: 3px;
    }

    &.field-datetime {
      white-space: normal;
      min-height: 29px;
    }

    &.field-boolean .options-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      min-height: 29px;

      .option:not(:last-child) {
        margin-right: 10px;
      }

      .option {
        display: flex;
        align-items: center;

        label {
          cursor: pointer;
          margin: 0;
          padding-left: 5px;;
        }
      }
    }
  }

  .edit-tools {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .icon {
      cursor: pointer;
      margin-left: 10px;
      font-size: 20px;
      color: #afafaf;
    }
    .icon:hover {
      color: #555555;
    }

    .icon-edit.active { color: #004a8d; }
    .icon-edit.active:hover { color: #0078d5; }

    .icon-rollback.active { color: #970000; }
    .icon-rollback.active:hover { color: #f10000; }

    .icon-save.active { color: #006d19; }
    .icon-save.active:hover { color: #09ba00; }
  }
</style>